import React, { useEffect, useState } from 'react';
import { GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { Supplier } from '../../../types';
import { SupplierSelectionForm } from '../SupplierSelectionForm';

interface SupplierEditCellProps extends GridRenderEditCellParams {
  api: GridRenderEditCellParams['api'];
  suppliers: Supplier[];
}

const SupplierEditCell: React.FC<SupplierEditCellProps> = (props) => {
  const { api, id, value, suppliers } = props;

  const findSupplier = (searchValue: string): Supplier | null => {
    if (!searchValue) return null;
    return (
      suppliers.find((s) => s.name === searchValue) ||
      suppliers.find((s) => s.id === searchValue) ||
      suppliers.find(
        (s) =>
          s.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          searchValue.toLowerCase().includes(s.name.toLowerCase())
      ) ||
      null
    );
  };

  const initialSupplier = findSupplier(value as string);

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(initialSupplier);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState<string>(
    initialSupplier ? initialSupplier.name : (value as string) || ''
  );

  // Re-sync only when the suppliers list itself changes (e.g. async load).
  // We intentionally exclude `value` from deps: every keystroke calls
  // api.setEditCellValue which updates props.value, which would trigger this
  // effect and call findSupplier() which does partial matching — causing the
  // input to snap back to a matched supplier name after every typed character.
  useEffect(() => {
    const newSupplier = findSupplier(value as string);
    setSelectedSupplier(newSupplier);
    setSupplierSearchQuery(newSupplier ? newSupplier.name : (value as string) || '');
  }, [suppliers]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = (name: string, supplier: Supplier | null) => {
    const currentRow = api.getRow(id);
    api.updateRows([
      {
        ...currentRow,
        id,
        supplierName: name,
        supplierId: supplier ? supplier.id : '',
        supplier
      }
    ]);
  };

  const handleInputChange = (e: React.SyntheticEvent<Element, Event>, inputValue: string | null) => {
    const finalInput = inputValue || '';
    setSupplierSearchQuery(finalInput);
    setSelectedSupplier(null);
    api.setEditCellValue({ id, field: 'supplierName', value: finalInput }, e);
    // Clear supplierId/supplier while the user is still typing
    updateRow(finalInput, null);
  };

  const handleSupplierSelectionChange = (
    e: React.SyntheticEvent<Element, Event>,
    newValue: Supplier | string | null
  ) => {
    let finalSupplier: Supplier | null = null;
    let finalName = '';

    if (typeof newValue === 'object' && newValue !== null) {
      finalSupplier = newValue;
      finalName = newValue.name;
    } else if (typeof newValue === 'string') {
      finalName = newValue;
      finalSupplier = suppliers.find((s) => s.name === finalName) || null;
    }
    // null (clear button) → both remain '' / null

    setSelectedSupplier(finalSupplier);
    setSupplierSearchQuery(finalName);
    api.setEditCellValue({ id, field: 'supplierName', value: finalName }, e);
    updateRow(finalName, finalSupplier);
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
      <div style={{ width: '100%' }}>
        <SupplierSelectionForm
          supplier={selectedSupplier}
          allSuppliers={suppliers}
          supplierSearchQuery={supplierSearchQuery}
          handleInputChange={handleInputChange}
          handleSupplierSelectionChange={handleSupplierSelectionChange}
          size="small"
          classNames="w-full"
          InputProps={{
            sx: {
              height: '100%',
              fontSize: '0.875rem',
              '& .MuiInputBase-input': { padding: '8px', height: 'auto' },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
            }
          }}
          InputLabelProps={{ sx: { display: 'none' } }}
        />
      </div>
    </div>
  );
};

export default SupplierEditCell;
